export interface TradeTimeline {
  available: boolean;
  imports: { events: number; activeMonths: number; recordedMonths: number };
  exports: { events: number; activeMonths: number; recordedMonths: number };
  importPaths: string[];
  exportPaths: string[];
  importPoints: Array<{ x: number; y: number; label: string; overlaps: boolean }>;
  exportPoints: Array<{ x: number; y: number; label: string; overlaps: boolean }>;
  ticks: Array<{ x: number; label: string }>;
  yTicks: Array<{ y: number; value: number }>;
  maxValue: number;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// The company API normally supplies "January 2025"; accept ISO months as well.
function monthIndex(name: unknown): number | null {
  if (typeof name !== 'string') return null;
  const monthName = name.trim().match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthName) {
    const month = MONTHS.findIndex(value => value.toLowerCase() === monthName[1].toLowerCase());
    return month < 0 ? null : Number(monthName[2]) * 12 + month;
  }
  const iso = name.trim().match(/^(\d{4})-(0[1-9]|1[0-2])(?:-\d{2})?$/);
  return iso ? Number(iso[1]) * 12 + Number(iso[2]) - 1 : null;
}

function monthLabel(index: number): string {
  return `${MONTHS[index % 12].slice(0, 3)} ${Math.floor(index / 12)}`;
}

export function buildTradeTimeline(trade: any): TradeTimeline {
  const read = (rows: any): Map<number, number> => {
    const data = new Map<number, number>();
    if (!Array.isArray(rows)) return data;
    for (const row of rows) {
      const month = monthIndex(row?.name);
      const value = Number(row?.value);
      if (month !== null && Number.isFinite(value) && value >= 0) data.set(month, value);
    }
    return data;
  };
  const imports = read(trade?.imports);
  const exports = read(trade?.exports);
  const months = [...new Set([...imports.keys(), ...exports.keys()])].sort((a, b) => a - b);
  const summary = (data: Map<number, number>) => ({
    events: [...data.values()].reduce((total, value) => total + value, 0),
    activeMonths: [...data.values()].filter(value => value > 0).length,
    recordedMonths: data.size,
  });
  const empty: TradeTimeline = {
    available: false, imports: summary(imports), exports: summary(exports),
    importPaths: [], exportPaths: [], importPoints: [], exportPoints: [], ticks: [], yTicks: [], maxValue: 0,
  };
  if (!months.length) return empty;

  const first = months[0];
  const last = months[months.length - 1];
  const maxValue = Math.max(1, ...imports.values(), ...exports.values());
  const x = (month: number) => 50 + (last === first ? 370 : (month - first) / (last - first) * 740);
  const y = (value: number) => 80 - value / maxValue * 64;
  const series = (data: Map<number, number>, other: Map<number, number>, name: string) => {
    const paths: string[] = [];
    const points: Array<{ x: number; y: number; label: string; overlaps: boolean }> = [];
    let path = '';
    let previous: number | null = null;
    for (const month of [...data.keys()].sort((a, b) => a - b)) {
      const value = data.get(month)!;
      const px = x(month);
      const py = y(value);
      if (previous !== null && month !== previous + 1) {
        if (path) paths.push(path);
        path = '';
      }
      path += `${path ? ' L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`;
      points.push({ x: px, y: py, label: `${name} · ${monthLabel(month)} · ${value} ${value === 1 ? 'event' : 'events'}`, overlaps: other.get(month) === value });
      previous = month;
    }
    if (path) paths.push(path);
    return { paths, points };
  };
  const importLine = series(imports, exports, 'Imports');
  const exportLine = series(exports, imports, 'Exports');
  const middle = Math.floor((first + last) / 2);
  return {
    available: true, imports: summary(imports), exports: summary(exports),
    importPaths: importLine.paths, exportPaths: exportLine.paths,
    importPoints: importLine.points, exportPoints: exportLine.points,
    ticks: [...new Set([first, middle, last])].map(month => ({ x: x(month), label: monthLabel(month) })),
    yTicks: [...new Set([0, Math.ceil(maxValue / 2), maxValue])].map(value => ({ y: y(value), value })),
    maxValue,
  };
}
